import { memo } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogTitle, IconButton, LinearProgress, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import ExportingState from 'components/ExportInformationAlert/ExportingState';
import { EXPORT_STATUS_ENUM } from 'modules/export/interfaces/export.enum';
import { ExportProgress } from 'modules/export/utils/export-progress.utils';

type DialogDownloadProps = {
  isOpen: boolean;
  onClose: any;
  error: any;
  onRetry?: () => void;
  status?: string;
  showTimeoutWarning?: boolean;
  onBackgroundDownload?: () => void;
  progress?: ExportProgress;
};

const DialogDownload = ({ isOpen, onClose, error, onRetry, status, showTimeoutWarning, onBackgroundDownload, progress }: DialogDownloadProps) => {
  const { t } = useTranslation('common');
  const isPending = status === EXPORT_STATUS_ENUM.PENDING;

  const handleBackgroundDownload = () => {
    onBackgroundDownload?.();
    onClose();
  };

  return (
    <Dialog
      maxWidth={'sm'}
      fullWidth
      open={isOpen}
      onClose={isPending ? undefined : onClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
    >
      <DialogTitle>
        {!isPending && (
          <IconButton
            aria-label='close'
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <ExportingState error={error} onRetry={onRetry} status={status} />
      {/* Antes solo habia un spinner mudo: el usuario no podia distinguir un
          export de 5 segundos de uno de media hora. Con los sellos de fase del
          backend se puede decir en que va y cuanto cabe esperar. */}
      {isPending && progress && progress.phase !== 'finished' && (
        <Box sx={{ mx: 5, mb: 2 }}>
          <Typography variant='caption' color='text.secondary' display='block' mb={0.5}>
            {progress.phase === 'queued'
              ? t('exportDialog.queued', { defaultValue: 'En cola, esperando un worker libre' })
              : t('exportDialog.rendering', { defaultValue: 'Generando el archivo' })}
            {progress.elapsedSeconds !== undefined &&
              ` · ${Math.floor(progress.elapsedSeconds / 60)}m ${progress.elapsedSeconds % 60}s`}
            {progress.expectedSeconds !== undefined &&
              !progress.overdue &&
              ` ${t('exportDialog.outOf', { defaultValue: 'de ~' })}${Math.ceil(progress.expectedSeconds / 60)}m`}
          </Typography>
          {/* Sin estimacion para el tipo, barra indeterminada: mas vale no
              fingir un porcentaje que inventarlo. */}
          <LinearProgress
            variant={progress.ratio === undefined ? 'indeterminate' : 'determinate'}
            value={progress.ratio === undefined ? undefined : progress.ratio * 100}
          />
          {progress.overdue && (
            <Typography variant='caption' color='warning.main' display='block' mt={0.5}>
              {t('exportDialog.overdue', {
                defaultValue: 'Tarda mas de lo habitual para este tipo de exportacion',
              })}
            </Typography>
          )}
        </Box>
      )}
      {isPending && showTimeoutWarning && (
        <Alert severity="warning" sx={{ mx: 5, mb: 2 }}>
          {t('exportDialog.timeoutWarning', { defaultValue: 'La descarga está tardando más de lo normal. Puede cerrar este modal y descargar en segundo plano.' })}
        </Alert>
      )}
      {isPending && (
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          {showTimeoutWarning && (
            <Button variant="contained" onClick={handleBackgroundDownload}>
              {t('exportDialog.closeAndDownloadBackground', { defaultValue: 'Cerrar y descargar en segundo plano' })}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default memo(DialogDownload);
